import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { Dropdown } from 'react-bootstrap'
import { useCurrencyExchangeRate } from 'shared/currency-exchange-rate/api/fetchCurrencyExchangeRate'
import notifyExchangeRateError from 'shared/currency-exchange-rate/components/notifyExchangeRateError'
import { AmountUnit, ConsignmentItemFormValues } from 'features/declaration/consignment-item/models/schema'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { useCalculatorModal } from 'features/declaration/form/components/calculator/CalculatorModalContext'
import FormCollapsibleColumn from '../../../../../../components/p5/FormCollapsibleColumn'
import FormField from '../../../../../../components/p5/FormField'
import Input from '../../../../../../components/p5/Input'
import FormSelect from '../../../../../../components/p5/FormSelect'
import { FieldRule, pickRule } from '../../../../models/fieldRules'
import CommodityCodeSearch from '../../../../consignment-item/components/CommodityCodeSearch'
import useDeclarationFieldRequirements
  from '../../../components/useDeclarationFieldRequirements'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'

function TransitGoodsData() {
  const { t } = useTranslation()
  const [, countriesOptions] = useCodelist('COUNTRIES')

  const { editorData } = useDeclarationEditorContext()

  const {
    register, setValue, control,
  } = useFormContext<ConsignmentItemFormValues>()

  const departureCustomsOffice = editorData?.departureCustomsOffice
  const country = useMemo(
    () => departureCustomsOffice?.slice('DEPARTURE_OFFICE_'.length, 'DEPARTURE_OFFICE_'.length + 2) ?? null,
    [departureCustomsOffice],
  )

  const [initialPriceCurrency, initialPrice, dutyRateUnit] = useWatch({
    control,
    name: ['initialPriceCurrency', 'initialPrice', 'dutyRateUnit'],
  })

  const [inputCurrencyCountry, setInputCurrencyCountry] = useState<string | null>(initialPriceCurrency)
  const { data: exchangeRate, error: exchangeRateError } = useCurrencyExchangeRate(inputCurrencyCountry)
  const { open } = useCalculatorModal()
  useEffect(() => {
    let price = initialPrice
    if (price !== null && exchangeRate && exchangeRate > 0) {
      price = Number.parseFloat((price * (1 / exchangeRate)).toFixed(3))
    }
    setValue('price', price)
  }, [initialPrice, exchangeRate, setValue])

  useEffect(() => {
    if (exchangeRateError) {
      notifyExchangeRateError(exchangeRateError)
    }
  }, [exchangeRateError])

  useEffect(() => {
    switch (initialPriceCurrency) {
      case 'EUR':
        setInputCurrencyCountry(null)
        break
      case 'USD':
        setInputCurrencyCountry('US')
        break
      case 'GBP':
        setInputCurrencyCountry('GB')
        break
      case 'RON':
        setInputCurrencyCountry('RO')
        break
      default:
        setInputCurrencyCountry(null)
    }
  }, [initialPriceCurrency])

  const fieldRequirements = useDeclarationFieldRequirements<ConsignmentItemFormValues>('consignmentItem', [
    'commodityCode',
    'commodityDescriptionOfGoods',
    'commodityHarmonizedSystemSubHeadingCode',
    'countryOfDestination',
    'countryOfDispatch',
    'price',
    'referenceNumberUCR',
    'dutyAmount',
    'dutyRate',
    'vatRate',
    'excise',
  ])

  const dutyRateOrAmountRequirement: FieldRule = pickRule(fieldRequirements.dutyRate, fieldRequirements.dutyAmount)

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-goods-data"
        headingLabel={t('declaration.p5.goodsData')}
      >
        <div className="ci-goods-main">
          <div className="ci-goods-col">
            <FormField
              labelKey="declaration.p5.referenceNumberUCR"
              fieldRule={fieldRequirements.referenceNumberUCR}
            >
              <Input
                <ConsignmentItemFormValues>
                type="text"
                field="referenceNumberUCR"
                labelKey="declaration.p5.referenceNumberUCR"
                placeholder={t('declaration.p5.referenceNumberUCR')}
                fieldRule={fieldRequirements.referenceNumberUCR}
              />
            </FormField>
            <FormField
              scope="commodityHarmonizedSystemSubHeadingCode"
              labelKey="declaration.p5.commodityCode"
              fieldRule={fieldRequirements.commodityHarmonizedSystemSubHeadingCode}
            >
              <CommodityCodeSearch country={country} isEns={false} />
            </FormField>
            <FormField
              labelKey="declaration.p5.goodsMeasureGrossMass"
              fieldRule={fieldRequirements.goodsMeasureGrossMass}
            >
              <div className="input-group">
                <Input
                  <ConsignmentItemFormValues>
                  field="goodsMeasureGrossMass"
                  labelKey="declaration.p5.goodsMeasureGrossMass"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={0.001}
                  placeholder={t('declaration.p5.goodsMeasureGrossMass')}
                  fieldRule={fieldRequirements.goodsMeasureGrossMass}
                />
                <div className="input-group-append">
                  <button
                    type="button"
                    className="btn btn-light h-100 border fw-bold"
                    onClick={() => {
                      open({
                        onResolve: (value: number) => {
                          setValue('goodsMeasureGrossMass', value)
                        },
                      })
                    }}
                  >
                    <i className="fas fa-calculator" />
                  </button>
                </div>
              </div>
            </FormField>
            <FormField
              labelKey="declaration.p5.goodsMeasureNetMass"
              fieldRule={fieldRequirements.goodsMeasureNetMass}
            >
              <Input
                <ConsignmentItemFormValues>
                field="goodsMeasureNetMass"
                labelKey="declaration.p5.goodsMeasureNetMass"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsMeasureNetMass')}
                fieldRule={fieldRequirements.goodsMeasureNetMass}
              />
            </FormField>
          </div>
          <div className="ci-goods-col">
            <FormField
              labelKey="declaration.p5.commodityDescriptionOfGoods"
              fieldRule={fieldRequirements.commodityDescriptionOfGoods}
            >
              <Input
                <ConsignmentItemFormValues>
                maxLength={250}
                type="textarea"
                field="commodityDescriptionOfGoods"
                labelKey="declaration.p5.commodityDescriptionOfGoods"
                placeholder={t('declaration.p5.commodityDescriptionOfGoods')}
                fieldRule={fieldRequirements.commodityDescriptionOfGoods}
              />
            </FormField>
            <FormField
              labelKey="declaration.p5.dispatchCountry"
              fieldRule={fieldRequirements.countryOfDispatch}
            >
              <FormSelect
                <ConsignmentItemFormValues>
                field="countryOfDispatch"
                labelKey="declaration.p5.dispatchCountry"
                options={countriesOptions}
                type="sync"
                fieldRule={fieldRequirements.countryOfDispatch}
              />
            </FormField>
            <FormField
              labelKey="declaration.p5.countryOfDestination"
              fieldRule={fieldRequirements.countryOfDestination}
            >
              <FormSelect
                <ConsignmentItemFormValues>
                field="countryOfDestination"
                labelKey="declaration.p5.countryOfDestination"
                options={countriesOptions}
                type="sync"
                fieldRule={fieldRequirements.countryOfDestination}
              />
            </FormField>
          </div>
        </div>
        <div className="ci-goods-taxes">
          <FormField
            labelKey="declaration.p5.goodsItemPrice"
            fieldRule={fieldRequirements.price}
          >
            <div className="input-group">
              <Input
                <ConsignmentItemFormValues>
                field="initialPrice"
                labelKey="declaration.p5.goodsItemPrice"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsItemPrice')}
              />
              <div className="input-group-append">
                <Dropdown
                  className="currency-picker"
                  onSelect={(eventKey) => {
                    if (eventKey) {
                      setValue('initialPriceCurrency', eventKey)
                    }
                  }}
                >
                  <Dropdown.Toggle variant="light" className="h-100 border">
                    {initialPriceCurrency}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item className="border border-bottom-0" eventKey="EUR">EUR</Dropdown.Item>
                    <Dropdown.Item className="border border-bottom-0" eventKey="USD">USD</Dropdown.Item>
                    <Dropdown.Item className="border border-bottom-0" eventKey="GBP">GBP</Dropdown.Item>
                    <Dropdown.Item className="border" eventKey="RON">RON</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
            <div className="input-group pt-1">
              {
                initialPriceCurrency !== 'EUR' && (
                  <div className="input-group">
                    <Input
                      <ConsignmentItemFormValues>
                      field="price"
                      labelKey="declaration.p5.goodsItemPrice"
                      type="number"
                      inputMode="decimal"
                      min={0}
                      step={0.001}
                      placeholder={t('declaration.p5.goodsItemPrice')}
                      fieldRule={fieldRequirements.price}
                      disabled
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">
                        {t('declaration.p5.goodsItemPrice', { context: 'unit' })}
                      </span>
                    </div>
                  </div>
                )
              }
            </div>
          </FormField>
          <FormField
            labelKey="declaration.p5.goodsItemDutyRate"
            fieldRule={dutyRateOrAmountRequirement}
          >
            <div className="input-group">
              <div className="input-group-prepend">
                <label className="input-group-text px-2" htmlFor="dutyRateUnit_PERCENTAGE">
                  <div className="form-check-inline m-0 p-0">
                    <input
                      className="form-check-input mx-1"
                      id="dutyRateUnit_PERCENTAGE"
                      type="radio"
                      value="PERCENTAGE"
                      {...register('dutyRateUnit' as const)}
                    />
                  </div>
                </label>
              </div>
              <Input
                field="dutyRate"
                labelKey="declaration.p5.goodsItemDutyRate"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsItemDutyRate')}
                disabled={dutyRateUnit === AmountUnit.AMOUNT}
                fieldRule={fieldRequirements.dutyRate}
              />
              <div className="input-group-append">
                <span className="input-group-text">
                  {t('declaration.p5.goodsItemDutyRate', { context: 'unit' })}
                </span>
              </div>
            </div>
            <div className="input-group pt-1">
              <div className="input-group-prepend">
                <label className="input-group-text px-2" htmlFor="dutyRateUnit_AMOUNT">
                  <div className="form-check-inline m-0 p-0">
                    <input
                      id="dutyRateUnit_AMOUNT"
                      type="radio"
                      className="form-check-input mx-1"
                      value="AMOUNT"
                      {...register('dutyRateUnit' as const)}
                    />
                  </div>
                </label>
              </div>
              <Input
                <ConsignmentItemFormValues>
                field="dutyAmount"
                labelKey="declaration.p5.goodsItemVatRate"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsItemDutyRate')}
                disabled={dutyRateUnit === AmountUnit.PERCENTAGE}
              />
              <div className="input-group-append">
                <span className="input-group-text">
                  {t('declaration.p5.goodsItemPrice', { context: 'unit' })}
                </span>
              </div>
            </div>
          </FormField>
          <FormField
            labelKey="declaration.p5.goodsItemVatRate"
            fieldRule={fieldRequirements.vatRate}
          >
            <div className="input-group">
              <Input
                <ConsignmentItemFormValues>
                field="vatRate"
                labelKey="declaration.p5.goodsItemVatRate"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsItemVatRate')}
                fieldRule={fieldRequirements.vatRate}
              />
              <div className="input-group-append">
                <span className="input-group-text">
                  {t('declaration.p5.goodsItemVatRate', { context: 'unit' })}
                </span>
              </div>
            </div>
          </FormField>
          <FormField
            labelKey="declaration.p5.goodsItemExcise"
            fieldRule={fieldRequirements.excise}
          >
            <div className="input-group">
              <Input
                <ConsignmentItemFormValues>
                field="excise"
                labelKey="declaration.p5.goodsItemExcise"
                type="number"
                inputMode="decimal"
                min={0}
                step={0.001}
                placeholder={t('declaration.p5.goodsItemExcise')}
                fieldRule={fieldRequirements.excise}
              />
              <div className="input-group-append">
                <span className="input-group-text">
                  {t('declaration.p5.goodsItemExcise', { context: 'unit' })}
                </span>
              </div>
            </div>
          </FormField>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default TransitGoodsData
